import React, { useState, useMemo, useEffect } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { useHandoffStore } from '../../store/handoffStore';
import { loadSetting, saveSetting } from '../../services/settingsService';

const GBU_KEY = 'gbu_eintraege_v1';

// ── Typen ─────────────────────────────────────────────────────────────────

type GefaehrdungsKategorie =
  | 'Mechanisch'
  | 'Elektrisch'
  | 'Chemisch'
  | 'Biologisch'
  | 'Physikalisch'
  | 'Ergonomisch'
  | 'Psychisch'
  | 'Brandschutz';

type RisikoStufe = 1 | 2 | 3 | 4 | 5;

interface GefaehrdungsEintrag {
  id:           string;
  bereich:      string;
  taetigkeit:   string;
  gefaehrdung:  string;
  kategorie:    GefaehrdungsKategorie;
  eintrittsWs:  RisikoStufe;  // 1=sehr selten … 5=häufig
  schwere:      RisikoStufe;  // 1=gering … 5=katastrophal
  massnahme:    string;
  massnahmeTyp: 'T' | 'O' | 'P';  // Technisch / Organisatorisch / Personenbezogen (TOP-Prinzip)
  erledigt:     boolean;
  frist:        string;        // ISO-Date
}

// risikozahl = eintrittsWs × schwere (1–25)
function risiko(e: GefaehrdungsEintrag): number {
  return e.eintrittsWs * e.schwere;
}

function risikoColor(r: number): string {
  if (r >= 15) return 'bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-400';
  if (r >= 8)  return 'bg-orange-100 text-orange-800 dark:bg-orange-950/30 dark:text-orange-400';
  if (r >= 4)  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-400';
  return 'bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400';
}

function risikoLabel(r: number): string {
  if (r >= 15) return 'Hoch';
  if (r >= 8)  return 'Mittel';
  if (r >= 4)  return 'Gering';
  return 'Sehr gering';
}

const KATEGORIEN: GefaehrdungsKategorie[] = [
  'Mechanisch', 'Elektrisch', 'Chemisch', 'Biologisch',
  'Physikalisch', 'Ergonomisch', 'Psychisch', 'Brandschutz',
];

// ── Demo-GBU-Einträge ─────────────────────────────────────────────────────

const DEMO_GBU: GefaehrdungsEintrag[] = [
  {
    id: '1', bereich: 'Produktion', taetigkeit: 'Maschinenbedienung',
    gefaehrdung: 'Quetschgefahr an beweglichen Maschinenteilen',
    kategorie: 'Mechanisch', eintrittsWs: 2, schwere: 4,
    massnahme: 'Schutzverkleidung nachrüsten, NOT-AUS prüfen',
    massnahmeTyp: 'T', erledigt: false, frist: '2026-07-01',
  },
  {
    id: '2', bereich: 'Büro', taetigkeit: 'Bildschirmarbeit',
    gefaehrdung: 'Übermäßige Bildschirmarbeitszeit, Sehdruck',
    kategorie: 'Ergonomisch', eintrittsWs: 4, schwere: 2,
    massnahme: '20-20-20-Regel kommunizieren, ergonomische Beratung',
    massnahmeTyp: 'O', erledigt: true, frist: '2026-06-01',
  },
  {
    id: '3', bereich: 'Lager', taetigkeit: 'Heben und Tragen',
    gefaehrdung: 'Wirbelsäulenbelastung durch schwere Lasten',
    kategorie: 'Ergonomisch', eintrittsWs: 3, schwere: 3,
    massnahme: 'Schulung Heben/Tragen, Hubwagen bereitstellen',
    massnahmeTyp: 'P', erledigt: false, frist: '2026-08-15',
  },
  {
    id: '4', bereich: 'Produktion', taetigkeit: 'Schweißen',
    gefaehrdung: 'Rauchgase, Partikelbelastung der Atemluft',
    kategorie: 'Chemisch', eintrittsWs: 3, schwere: 4,
    massnahme: 'Absauganlage kontrollieren, PSA Atemschutz FFP3',
    massnahmeTyp: 'T', erledigt: false, frist: '2026-07-15',
  },
  {
    id: '5', bereich: 'Alle Bereiche', taetigkeit: 'Allgemeiner Betrieb',
    gefaehrdung: 'Psych. Belastung durch Zeitdruck und Überstunden',
    kategorie: 'Psychisch', eintrittsWs: 3, schwere: 3,
    massnahme: 'Stressmanagement-Workshop, Überstundenkontrolle',
    massnahmeTyp: 'O', erledigt: false, frist: '2026-09-01',
  },
];

// ── Neuer GBU-Eintrag ─────────────────────────────────────────────────────

function NeuerGbuEintrag({ onAdd }: { onAdd: (e: GefaehrdungsEintrag) => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    bereich: '', taetigkeit: '', gefaehrdung: '',
    kategorie: 'Mechanisch' as GefaehrdungsKategorie,
    eintrittsWs: 3 as RisikoStufe, schwere: 3 as RisikoStufe,
    massnahme: '', massnahmeTyp: 'T' as 'T' | 'O' | 'P',
    frist: '',
  });

  const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]): void => {
    setForm(p => ({ ...p, [key]: val }));
  };

  const handleAdd = (): void => {
    if (!form.bereich || !form.gefaehrdung || !form.frist) return;
    onAdd({
      id:          crypto.randomUUID(),
      bereich:     form.bereich,
      taetigkeit:  form.taetigkeit,
      gefaehrdung: form.gefaehrdung,
      kategorie:   form.kategorie,
      eintrittsWs: form.eintrittsWs,
      schwere:     form.schwere,
      massnahme:   form.massnahme,
      massnahmeTyp: form.massnahmeTyp,
      erledigt:    false,
      frist:       form.frist,
    });
    setOpen(false);
    setForm({ bereich:'', taetigkeit:'', gefaehrdung:'', kategorie:'Mechanisch',
      eintrittsWs:3, schwere:3, massnahme:'', massnahmeTyp:'T', frist:'' });
  };

  if (!open) {
    return (
      <div className="flex justify-end">
        <button onClick={() => { setOpen(true); }} className="btn-secondary text-sm">
          + Neue Gefährdung
        </button>
      </div>
    );
  }

  return (
    <div className="card space-y-3 border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Neue Gefährdung</h3>
        <button onClick={() => { setOpen(false); }} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="label-text">Bereich <span className="text-red-500">*</span></label>
          <input value={form.bereich} onChange={e => { set('bereich', e.target.value); }}
            placeholder="z.B. Produktion" className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Tätigkeit</label>
          <input value={form.taetigkeit} onChange={e => { set('taetigkeit', e.target.value); }}
            placeholder="z.B. Maschinenbedienung" className="input-field" />
        </div>
        <div className="space-y-1 col-span-2">
          <label className="label-text">Gefährdungsbeschreibung <span className="text-red-500">*</span></label>
          <input value={form.gefaehrdung} onChange={e => { set('gefaehrdung', e.target.value); }}
            placeholder="Beschreiben Sie die Gefährdung" className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Kategorie</label>
          <select value={form.kategorie}
            onChange={e => { set('kategorie', e.target.value as GefaehrdungsKategorie); }}
            className="input-field">
            {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="label-text">Maßnahmentyp (TOP)</label>
          <select value={form.massnahmeTyp}
            onChange={e => { set('massnahmeTyp', e.target.value as 'T'|'O'|'P'); }}
            className="input-field">
            <option value="T">T — Technisch</option>
            <option value="O">O — Organisatorisch</option>
            <option value="P">P — Personenbezogen</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="label-text">Eintrittswahrsch. (1–5)</label>
          <input type="range" min={1} max={5} value={form.eintrittsWs}
            onChange={e => { set('eintrittsWs', parseInt(e.target.value) as RisikoStufe); }}
            className="w-full" />
          <p className="text-xs text-gray-500 text-center">{form.eintrittsWs} — {['','Sehr selten','Selten','Möglich','Wahrscheinlich','Häufig'][form.eintrittsWs]}</p>
        </div>
        <div className="space-y-1">
          <label className="label-text">Schadenschwere (1–5)</label>
          <input type="range" min={1} max={5} value={form.schwere}
            onChange={e => { set('schwere', parseInt(e.target.value) as RisikoStufe); }}
            className="w-full" />
          <p className="text-xs text-gray-500 text-center">{form.schwere} — {['','Gering','Leicht','Mäßig','Schwer','Katastrophal'][form.schwere]}</p>
        </div>
        <div className="space-y-1 col-span-2">
          <label className="label-text">Maßnahme</label>
          <input value={form.massnahme} onChange={e => { set('massnahme', e.target.value); }}
            placeholder="Beschreiben Sie die Maßnahme" className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Frist <span className="text-red-500">*</span></label>
          <input type="date" value={form.frist} onChange={e => { set('frist', e.target.value); }}
            className="input-field" />
        </div>
        <div className="flex items-end">
          <div className={`text-sm font-bold px-3 py-2 rounded-lg ${risikoColor(form.eintrittsWs * form.schwere)}`}>
            Risiko: {form.eintrittsWs * form.schwere} — {risikoLabel(form.eintrittsWs * form.schwere)}
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setOpen(false); }} className="btn-secondary text-sm">Abbrechen</button>
        <button onClick={handleAdd}
          disabled={!form.bereich || !form.gefaehrdung || !form.frist}
          className="btn-primary text-sm disabled:opacity-50">
          Eintrag hinzufügen
        </button>
      </div>
    </div>
  );
}

// ── Risikomatrix-Tabelle ──────────────────────────────────────────────────

function Risikomatrix(): React.JSX.Element {
  const LABELS_WS = ['', 'Sehr selten', 'Selten', 'Möglich', 'Wahrscheinlich', 'Häufig'];
  const LABELS_SW = ['', 'Gering', 'Leicht', 'Mäßig', 'Schwer', 'Katastrophal'];

  const cellColor = (r: number): string => {
    if (r >= 15) return 'bg-red-200 dark:bg-red-900/40 text-red-900 dark:text-red-200';
    if (r >= 8)  return 'bg-orange-200 dark:bg-orange-900/30 text-orange-900 dark:text-orange-200';
    if (r >= 4)  return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-200';
    return 'bg-green-100 dark:bg-green-900/20 text-green-900 dark:text-green-200';
  };

  return (
    <div className="card overflow-x-auto">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Risikomatrix (Eintritt × Schwere)</h2>
      <table className="text-xs text-center border-collapse">
        <thead>
          <tr>
            <th className="p-2 text-gray-400 font-normal">E \ S</th>
            {[1,2,3,4,5].map(s => (
              <th key={s} className="p-2 text-gray-500 font-semibold">{LABELS_SW[s] ?? s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[5,4,3,2,1].map(ws => (
            <tr key={ws}>
              <th className="p-2 text-gray-500 font-semibold text-right pr-3">{LABELS_WS[ws] ?? ws}</th>
              {[1,2,3,4,5].map(s => {
                const r = ws * s;
                return (
                  <td key={s} className={`p-2 w-20 font-bold rounded-sm ${cellColor(r)}`}>
                    {r}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── GBU-Liste ──────────────────────────────────────────────────────────────

function GbuListe({ eintraege, onToggle, onDelete }: {
  eintraege: GefaehrdungsEintrag[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}): React.JSX.Element {
  const [filterKat, setFilterKat] = useState<GefaehrdungsKategorie | 'Alle'>('Alle');

  const filtered = useMemo(() =>
    eintraege.filter(e => filterKat === 'Alle' || e.kategorie === filterKat)
  , [eintraege, filterKat]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {(['Alle', ...KATEGORIEN] as const).map(k => (
          <button key={k}
            onClick={() => { setFilterKat(k); }}
            className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
              ${filterKat === k
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}>
            {k}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Gefährdung</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3 hidden md:table-cell">Bereich</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Risiko</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3 hidden lg:table-cell">Maßnahme</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3 hidden lg:table-cell">Frist</th>
              <th className="w-10 px-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map(e => {
              const r = risiko(e);
              return (
                <tr key={e.id}
                  className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50
                    ${e.erledigt ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{e.gefaehrdung}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{e.taetigkeit} · {e.kategorie}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{e.bereich}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${risikoColor(r)}`}>
                      {r} — {risikoLabel(r)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500 hidden lg:table-cell max-w-48">
                    <span className={`inline-block mr-1 font-bold
                      ${e.massnahmeTyp === 'T' ? 'text-blue-600' : e.massnahmeTyp === 'O' ? 'text-purple-600' : 'text-orange-600'}`}>
                      {e.massnahmeTyp}
                    </span>
                    {e.massnahme}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-400 hidden lg:table-cell whitespace-nowrap">{e.frist}</td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-1">
                      <input type="checkbox" checked={e.erledigt}
                        onChange={() => { onToggle(e.id); }}
                        className="rounded border-gray-300 text-green-600"
                        title="Als erledigt markieren" />
                      <button onClick={() => { onDelete(e.id); }}
                        title="Löschen"
                        className="text-gray-300 hover:text-red-500 transition-colors px-0.5">
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

type AsaTab = 'gbu' | 'matrix' | 'protokoll';

export function AsaModul(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const requestHandoff = useHandoffStore(s => s.request);
  const [tab, setTab] = useState<AsaTab>('gbu');
  const [eintraege, setEintraege] = useState<GefaehrdungsEintrag[]>([]);

  useEffect(() => {
    void loadSetting<GefaehrdungsEintrag[]>(GBU_KEY, DEMO_GBU).then(setEintraege);
  }, []);

  const persist = (updated: GefaehrdungsEintrag[]): void => {
    setEintraege(updated);
    void saveSetting(GBU_KEY, updated);
  };

  const toggle = (id: string): void => {
    persist(eintraege.map(e => e.id === id ? { ...e, erledigt: !e.erledigt } : e));
  };

  const offenHoch = eintraege.filter(e => !e.erledigt && risiko(e) >= 15).length;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1">
        {([
          { key: 'gbu'      as AsaTab, label: 'GBU',          icon: '📋' },
          { key: 'matrix'   as AsaTab, label: 'Risikomatrix',  icon: '🟥' },
          { key: 'protokoll' as AsaTab, label: 'ASA-Protokoll', icon: '🛡' },
        ]).map(t => (
          <button key={t.key}
            onClick={() => { setTab(t.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === t.key
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
            <span>{t.icon}</span>
            <span>{t.label}</span>
            {t.key === 'gbu' && offenHoch > 0 && (
              <span className="text-xs bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400 px-1.5 py-0.5 rounded-full">
                {offenHoch} Hoch
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">ASA / GBU</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Gefährdungsbeurteilung · {mandant !== null && mandant !== undefined ? mandant.name : 'Alle Mandanten'}
            </p>
          </div>
          <button onClick={() => { window.print(); }} className="btn-secondary text-sm">
            🖨 Drucken
          </button>
        </div>

        {tab === 'gbu' && (
          <>
            <NeuerGbuEintrag onAdd={e => { persist([...eintraege, e]); }} />
            <div className="grid grid-cols-3 gap-4">
              <div className="card">
                <p className="text-xs text-gray-500">Gefährdungen gesamt</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{eintraege.length}</p>
              </div>
              <div className="card">
                <p className="text-xs text-gray-500">Offen (hoch)</p>
                <p className={`text-2xl font-bold mt-1 ${offenHoch > 0 ? 'text-red-600' : 'text-green-600 dark:text-green-400'}`}>
                  {offenHoch}
                </p>
              </div>
              <div className="card">
                <p className="text-xs text-gray-500">Erledigt</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {eintraege.filter(e => e.erledigt).length}
                </p>
              </div>
            </div>
            <GbuListe eintraege={eintraege} onToggle={toggle}
              onDelete={id => { persist(eintraege.filter(e => e.id !== id)); }} />
            <p className="text-xs text-gray-400 text-center">
              TOP-Prinzip: <span className="text-blue-600 font-medium">T</span>=Technisch ·{' '}
              <span className="text-purple-600 font-medium">O</span>=Organisatorisch ·{' '}
              <span className="text-orange-600 font-medium">P</span>=Personenbezogen
            </p>
          </>
        )}
        {tab === 'matrix'    && <Risikomatrix />}
        {tab === 'protokoll' && (
          <div className="card text-center py-14 text-gray-400 space-y-3">
            <p className="text-4xl">🛡</p>
            <p className="font-medium text-gray-600 dark:text-gray-300">ASA-Protokoll</p>
            <p className="text-sm">Das ASA-Sitzungsprotokoll wird im Formulare-Modul ausgefüllt und gedruckt.</p>
            <button
              onClick={() => { requestHandoff('formulare', { action: 'formular', formularTyp: 'ASA-Protokoll', quelle: {} }); }}
              className="btn-primary text-sm mx-auto">
              📄 ASA-Protokoll öffnen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
